import {
  TextInput,
  Select,
  Switch,
  Text,
  Avatar,
  Textarea,
  Group,
} from "@mantine/core";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { IconTrash, IconPencil, IconSettings } from "@tabler/icons";
import { DATA_AGE_TYPES, DATA_COUNTRY } from "../../data/options";
import FileList from "../../components/fileList";
import ActivityLog from "../../components/activityLog.js";
import ImagePicker from "../../components/imagePicker";

import ContactList from "../../components/contactList";
import { useCellRender } from "../../hooks/useCellRender";
import TextEditor from "../../components/textEditor.js";
import OrderList from "../../components/orderList.js";
import LoginInfoRow from "../../components/loginInfoRow.js";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";
import LinkageList from "../../components/linkageList.js";
import { useAuthUser } from "react-auth-kit";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LinkageRow from "../../components/linkageRow.js";
import ProviderCommissionScheduleTab from "../../components/providerCommissionScheduleTab";
import ContactInfoRow from "../../components/contactInfoRow.js";
import ProviderEmailPurposeField from "../../components/providerEmailPurposeField";
import FormConfirmation from "../../components/formConfirmation";
import ProductCategoryCheckboxGroup from "../../components/productCategoryCheckboxGroup";

const apiEntity = "provider";

const PROVIDER_CONFIRM_FIELD_LABELS = {
  code: "Code",
  shortName: "Short Name",
  name: "English Name",
  ageType: "Age Type",
};

const requiredField =
  (label) =>
  (value) => {
    if (value === null || value === undefined) return `${label} is required`;
    if (!String(value).trim()) return `${label} is required`;
    return null;
  };

const providerValidate = {
  code: requiredField("Code"),
  shortName: requiredField("Short Name"),
  name: requiredField("English Name"),
  ageType: requiredField("Age Type"),
  email: (value) =>
    !value || /^\S+@\S+$/.test(value) ? null : "Invalid email",
};
const providerImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadTemplate`;
const providerImportExistingTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadExistingTemplate`;
const providerLoginImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadLoginTemplate`;
const providerAllLoginImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadAllLoginTemplate`;
const providerAllLoginImportExistingTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadAllLoginExistingTemplate`;
const providerAllContactImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadAllContactTemplate`;
const providerAllContactImportExistingTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/provider/downloadAllContactExistingTemplate`;

const handleLoginInfoImport = async (file, form, api) => {
  const result = await api.Provider.importLoginBatch(file);
  if (!result?.success) {
    throw result?.error ?? result?.message ?? "Import failed";
  }
  return result.data?.loginInfo ?? [];
};

function Provider() {
  const [cellRender] = useCellRender();

  const auth = useAuthUser();
  const userRole = auth().userRole;
  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const providerLink =
    userRole.entityLinks.find((l) => l.entity === "provider")?.link ??
    "/gi/provider";

  // Redirect within this tab if role link differs from current route
  React.useEffect(() => {
    if (currentPath !== providerLink) {
      navigate(providerLink, { replace: true });
    }
  }, [providerLink, currentPath, navigate]);

  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "code",
        sortable: true,
        width: 160,
        cellRender: (col, data) => (
          <Group spacing={4} noWrap>
            <Text size="xs">{data.code}</Text>
            <McaApprovalListBadge entity={data} compact />
          </Group>
        ),
      },
      {
        field: "name",
        sortable: true,
        width: 300,
        cellRender: (col, data) => cellRender.ProviderInfo(data),
      },
      // { field: "name", sortable: true },
      { field: "tel", sortable: true },
      { field: "fax", sortable: true },
      { field: "email", sortable: true },
      {
        field: "productCategories",
        headerName: "Business Lines",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "provider",
          field: "productCategories",
          label: "Business Lines",
          populate: {
            model: "ProductCategory",
            select: "name cname code",
            labelField: "name",
            sort: { name: 1 },
          },
          width: 280,
          height: 220,
        },
      },
      {
        field: "isActive",
        headerName: "Status",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "provider",
          field: "isActive",
          label: "Status",
          staticValues: [
            { label: "Active", value: true },
            { label: "Inactive", value: false },
          ],
          width: 200,
          height: 160,
        },
      },
      {
        field: "ageType",
        headerName: "Age Type",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "provider",
          field: "ageType",
          label: "Age Type",
          staticValues: DATA_AGE_TYPES,
          width: 280,
          height: 180,
        },
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {
              //   color:"red"
            },
          },
        ],
        actionMenu: [
          {
            name: "delete",
            label: "Delete data",
            icon: IconTrash,
            props: {
              color: "red",
            },
          },
        ],
      },
    ],
    searchableFields: ["code", "name", "cname", "email"],
  };
  return (
    <PageList
      title={"Provider List"}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      initSort={{ by: "code", order: "asc" }}
      importVariants={[
        {
          key: "provider",
          label: "Provider",
          templateDownloads: [
            {
              url: providerImportTemplateUrl,
              text: "Download Blank Template",
            },
            {
              url: providerImportExistingTemplateUrl,
              text: "Download Existing Providers",
            },
          ],
        },
        {
          key: "portalLogins",
          label: "Portal Logins",
          templateDownloads: [
            {
              url: providerAllLoginImportTemplateUrl,
              text: "Download Blank Template",
            },
            {
              url: providerAllLoginImportExistingTemplateUrl,
              text: "Download Existing Logins",
            },
          ],
          importModes: [
            { value: "insert", label: "Insert (append)" },
            {
              value: "replace",
              label: "Replace (overwrite per provider)",
            },
          ],
          defaultImportMode: "insert",
          onImport: (file, { importMode }, api) =>
            api.Provider.importAllLoginBatch(file, { importMode }),
        },
        {
          key: "providerContacts",
          label: "Provider Contacts",
          templateDownloads: [
            {
              url: providerAllContactImportTemplateUrl,
              text: "Download Blank Template",
            },
            {
              url: providerAllContactImportExistingTemplateUrl,
              text: "Download Existing Contacts",
            },
          ],
          importModes: [
            { value: "insert", label: "Insert (append)" },
            {
              value: "replace",
              label: "Replace (overwrite per provider)",
            },
          ],
          defaultImportMode: "insert",
          onImport: (file, { importMode }, api) =>
            api.Provider.importAllContactBatch(file, { importMode }),
        },
      ]}
    />
  );
}

export default Provider;

export const getProviderEditFormSchema = () => ({
  title: "Provider",
  titleField: "shortName",
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
    size: "xl", //xs, sm, xl  ....100%
    // position:"right"
  },
  apiEntity: "provider",
  showSaveBar: false,

  validate: providerValidate,

  layout: {
    containers: [
      {
        key: "main-tab",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default", //default | pills | outline
          defaultValue: "Overview",
        },
        tab: [
          {
            key: "tab-overview",
            label: "Overview",
          },
          {
            key: "tab-files",
            label: "Files",
          },
          {
            key: "tab-log",
            label: "Logs",
          },
        ],
      },

      {
        key: "section-info",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Provider Info",
          description: "Please fill in the basic information of provider",
          withBorder: false,
          mt: 20,
        },
      },

      {
        key: "basicInfo-g",
        parent: "section-info",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        props: {
          columns: 24,
        },
        cols: [
          {
            key: "basicInfo-gap1",
            props: {
              span: 1,
            },
          },
          {
            key: "basicInfo-g0",
            props: {
              span: 4,
            },
          },
          {
            key: "basicInfo-gap2",
            props: {
              span: 1,
            },
          },
          {
            key: "basicInfo-g1",
            props: {
              span: 9,
            },
          },
          {
            key: "basicInfo-g2",
            props: {
              span: 9,
            },
          },
        ],
      },

      {
        key: "section-business-lines",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Business Lines",
          description: "Select the business lines this provider handles",
          withBorder: false,
          mt: 20,
        },
      },

      {
        key: "section-resource",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Resource Info",
          description: "",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },

      {
        key: "resources",
        parent: "section-resource",
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default", //default | pills | outline
          defaultValue: "Contact List",
        },
        tab: [
          {
            key: "tab-contact",
            label: "Contact List",
          },
          {
            key: "tab-login",
            label: "Portal Login",
          },
          {
            key: "tab-paymentInfo",
            label: "Payment Info",
          },
          {
            key: "tab-providerFormFile",
            label: "Form Files",
          },
          {
            key: "tab-providerFormLinkage",
            label: "Form Linkage",
          },
          {
            key: "tab-commissionSchedule",
            label: "Commission Schedule",
          },
        ],
      },

      {
        key: "section-contactList",
        parent: "tab-contact",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          description: "Please fill in the contact details",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },

      {
        key: "section-providerLogin",
        parent: "tab-login",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          description: "Please fill in the provider login",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },

      {
        key: "section-paymentInfo",
        parent: "tab-paymentInfo",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          description: "Please fill in the payment Ifno",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },

      {
        key: "section-providerFormFile",
        parent: "tab-providerFormFile",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          description: "Please fill in the provider form Info",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },
      {
        key: "section-providerFormLinkage",
        parent: "tab-providerFormLinkage",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          description: "Please fill in the provider form Info",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },

      {
        key: "section-commissionSchedule",
        parent: "tab-commissionSchedule",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },

      {
        key: "section-files",
        parent: "tab-files",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Files",
          titleOrder: 5,
          // description: "Please assign the corresponding consultant",
          withBorder: false,
          minHeight: "0px",
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-log",
        parent: "tab-log",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Logs",
          titleOrder: 5,
          // description: "Please assign the corresponding consultant",
          withBorder: false,
          minHeight: "0px",
          mt: 20,
          mb: 20,
        },
      },
    ],

    fields: [
      {
        name: "files",
        component: FileList,
        parent: "section-files",
        props: {
          tableMarginBottom: 50,
        },
      },
      {
        name: "log",
        component: ActivityLog,
        parent: "section-log",
        props: {},
      },
      {
        name: "image",
        component: ImagePicker,
        parent: "basicInfo-g0",
        props: {
          size: 160,
        },
      },
      {
        name: "code",
        component: TextInput,
        parent: "basicInfo-g1",
        props: {
          required: true,
          label: "Code",
          placeholder: "AXA",
        },
      },

      {
        name: "shortName",
        component: TextInput,
        parent: "basicInfo-g1",
        props: {
          required: true,
          label: "Short Name",
          placeholder: "AXA",
        },
      },

      {
        name: "name",
        component: TextInput,
        parent: "basicInfo-g1",

        props: {
          required: true,
          label: "English Name",
          placeholder:
            "AXA China Region Insurance Company (Hong Kong) Limited",
        },
      },
      {
        name: "cname",
        parent: "basicInfo-g1",
        component: TextInput,
        props: {
          label: "Chinese Name  ",
          placeholder: "安盛保險有限公司",
        },
      },

      {
        name: "insurerCode",
        component: TextInput,
        parent: "basicInfo-g1",
        props: {
          label: "Broker Code",
          placeholder: "060",
        },
      },

      {
        name: "tel",
        component: TextInput,
        parent: "basicInfo-g2",
        props: {
          label: "Primay Contact",
          placeholder: "31052388",
        },
      },
      {
        name: "fax",
        component: TextInput,
        parent: "basicInfo-g2",
        props: {
          label: "Fax",
          placeholder: "31052399",
        },
      },

      {
        name: "email",
        component: TextInput,
        parent: "basicInfo-g2",
        props: {
          label: "Email",
          placeholder: "help@aia.com",
        },
      },
      {
        name: "emailPurpose",
        component: ProviderEmailPurposeField,
        parent: "basicInfo-g2",
        props: {
          label: "Email purpose",
          placeholder: "Counts as General when empty",
        },
      },
      {
        name: "website",
        component: TextInput,
        parent: "basicInfo-g2",
        props: {
          label: "Website",
          placeholder: "https://www.aia.com.hk",
        },
      },
      {
        name: "address",
        component: Textarea,
        parent: "basicInfo-g2",
        props: {
          label: "Address",
          placeholder: "",
          minRows: 8,
          autosize: true,
        },
      },

      {
        name: "isActive",
        component: Switch,
        parent: "basicInfo-g2",
        props: {
          type: "checkbox",

          label: "Active",
          mt: 10,
        },
      },

      {
        name: "ageType",
        component: Select,
        parent: "basicInfo-g1",
        props: {
          required: true,
          label: "Age Type",
          placeholder: "",
          data: DATA_AGE_TYPES,
          default: "PREVIOUS",
        },
      },

      {
        name: "remark",
        component: Textarea,
        parent: "basicInfo-g1",
        props: {
          label: "Remark",
          placeholder: "",
          minRows: 2,
          autosize: true,
        },
      },

      {
        name: "productCategories",
        component: ProductCategoryCheckboxGroup,
        parent: "section-business-lines",
        props: {},
      },

      // {
      //   name: "contacts",
      //   component: ContactList,
      //   parent: "section-contactList",
      //   props: {},
      // },
      {
        name: "contacts",
        component: OrderList,
        parent: "section-contactList",
        props: {
          onEnterSave: false,
          defaultRowValue: {},
          row: {
            component: ContactInfoRow,
          },
        },
      },

      {
        name: "paymentInfo",
        component: TextEditor,
        parent: "section-paymentInfo",
        props: {
          readOnly: false,
        },
      },
      {
        name: "loginInfo",
        component: OrderList,
        parent: "section-providerLogin",
        props: {
          onEnterSave: false,
          defaultRowValue: {},
          showImport: true,
          importTemplateDownloads: [
            {
              getUrl: () => providerLoginImportTemplateUrl,
              text: "Download Blank Template",
            },
            {
              getUrl: (form) =>
                form.values._id
                  ? `${providerLoginImportTemplateUrl}/${form.values._id}`
                  : null,
              text: "Download Existing Logins",
            },
          ],
          onImport: handleLoginInfoImport,
          row: {
            component: LoginInfoRow,
          },
        },
      },

      {
        name: "formFiles",
        component: FileList,
        parent: "section-providerFormFile",
        props: {
          uploadLabel: "Upload Form Files",
        },
      },

      // {
      //   name: "formLinkage",
      //   component: LinkageList,
      //   parent: "section-providerFormLinkage",
      //   props: {

      //   },
      // },
      {
        name: "formLinkage",
        component: OrderList,
        parent: "section-providerFormLinkage",
        props: {
          row: {
            component: LinkageRow,
          },
        },
      },

      {
        name: "commissionScheduleView",
        component: ProviderCommissionScheduleTab,
        parent: "section-commissionSchedule",
        props: {
          version: "COMPANY",
          showGrossRateOnly: false,
          rateFactor: 1,
        },
      },
    ],
  },
});

const addFormSchema = {
  title: "Provider",
  // titleField: "code",
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: 1080,
  },
  apiEntity: "provider",
  showSaveBar: false,

  initialValues: {
    code: "",
    name: "",
    cname: "",
    ageType: "PREVIOUS",
    email: "",
    website: "",
    remark: "",
    productCategories: [],
    isActive: true,
    // termsOfService: true,
  },

  validate: providerValidate,

  layout: {
    containers: [
      {
        key: "t1",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
        props: {
          variant: "default", //default | pills | outline
          defaultValue: "Basic Info",
          orientation: "vertical",
          style: { minHeight: "400px" },
        },
        steps: [
          { key: "s1", label: "Basic Info", icon: IconSettings },
          { key: "s2", label: "Contact", icon: IconSettings },
          { key: "s4", label: "Confirm", icon: IconSettings },
        ],
      },

      {
        key: "section-basic",
        parent: "s1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Basic",
          description: "Please fill in the detail of the provider",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },
      {
        key: "basic-g",
        parent: "section-basic",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          {
            key: "basic-g1",
            props: {
              span: 2,
              mt: 0,
            },
          },
          {
            key: "basic-g2",
            props: {
              span: 5,
            },
          },
          {
            key: "basic-g3",
            props: {
              span: 5,
            },
          },
        ],
      },

      {
        key: "section-contact",
        parent: "s2",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Contact Info",
          description: "Please fill in the address details",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },
      {
        key: "contact-g",
        parent: "section-contact",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          {
            key: "contact-g1",
            props: {
              span: 6,
            },
          },
          {
            key: "contact-g2",
            props: {
              span: 6,
            },
          },
        ],
      },

      {
        key: "section-confirmt",
        parent: "s4",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Confirm",
          description:
            "Thank you. Please click confirm button to apply the changes.",
          withBorder: false,
          ml: 10,
          mt: 0,
        },
      },
    ],

    fields: [
      {
        name: "code",
        component: TextInput,
        parent: "basic-g2",
        props: {
          required: true,
          label: "Code",
          placeholder: "AXA",
        },
      },

      {
        name: "image",
        component: ImagePicker,
        parent: "basic-g1",
        props: {},
      },
      {
        name: "shortName",
        component: TextInput,
        parent: "basic-g2",
        props: {
          required: true,
          label: "Short Name",
          placeholder: "AXA",
        },
      },

      {
        name: "name",
        component: TextInput,
        parent: "basic-g2",

        props: {
          required: true,
          label: "English Name",
          placeholder:
            "AXA China Region Insurance Company (Hong Kong) Limited",
        },
      },
      {
        name: "cname",
        parent: "basic-g2",
        component: TextInput,
        props: {
          label: "Chinese Name  ",
          placeholder: "安盛保險有限公司",
        },
      },
      {
        name: "insurerCode",
        component: TextInput,
        parent: "basic-g3",
        props: {
          label: "Insurer Code",
          placeholder: "060",
        },
      },
      {
        name: "ageType",
        component: Select,
        parent: "basic-g3",
        props: {
          required: true,
          label: "Age Type",
          placeholder: "",
          data: DATA_AGE_TYPES,
          default: "PREVIOUS",
        },
      },

      {
        name: "remark",
        component: Textarea,
        parent: "basic-g3",
        props: {
          label: "Remark",
          placeholder: "",
          minRows: 2,
          autosize: true,
        },
      },

      {
        name: "productCategories",
        component: ProductCategoryCheckboxGroup,
        parent: "section-basic",
        props: {
          label: "Business Lines",
        },
      },

      {
        name: "tel",
        component: TextInput,
        parent: "contact-g1",
        props: {
          label: "Primay Contact",
          placeholder: "31052388",
        },
      },
      {
        name: "fax",
        component: TextInput,
        parent: "contact-g1",
        props: {
          label: "Fax",
          placeholder: "31052399",
        },
      },

      {
        name: "email",
        component: TextInput,
        parent: "contact-g1",
        props: {
          label: "Email",
          placeholder: "help@aia.com",
        },
      },
      {
        name: "emailPurpose",
        component: ProviderEmailPurposeField,
        parent: "contact-g1",
        props: {
          label: "Email purpose",
          placeholder: "Counts as General when empty",
        },
      },
      {
        name: "website",
        component: TextInput,
        parent: "contact-g1",
        props: {
          label: "Website",
          placeholder: "https://www.aia.com.hk",
        },
      },
      {
        name: "address",
        component: Textarea,
        parent: "contact-g2",
        props: {
          label: "Address",
          placeholder: "",
          minRows: 8,
          autosize: true,
        },
      },

      // {
      // 	name: "contacts",
      // 	component: ContactList,
      // 	parent: "section-contact",
      // 	props: {},
      // },

      {
        name: "confirm",
        component: FormConfirmation,
        parent: "section-confirmt",
        props: {
          validateOnMount: true,
          fieldLabels: PROVIDER_CONFIRM_FIELD_LABELS,
          successText: [
            "Thank you. Please click confirm button to apply the changes.",
          ],
        },
      },
    ],
  },
};

const formSchema = {
  add: addFormSchema,
  edit: getProviderEditFormSchema(),
};
